import apiClient from './api/apiClient.js';

class OnRampService {
  /**
   * Get quotes from all providers (Onramper-style format)
   * Uses the consolidated GET /quotes endpoint
   * @param {Object} params - Quote parameters { fiatCurrency, cryptoCurrency, fiatAmount, network, isBuyOrSell }
   * @returns {Promise<Array>} Array of quotes from all providers
   */
  async getAllQuotes(params) {
    const queryParams = {
      source: params.fiatCurrency,
      destination: params.cryptoCurrency,
      amount: params.fiatAmount || params.cryptoAmount,
      network: params.network,
      type: (params.isBuyOrSell || 'BUY').toLowerCase(),
      paymentMethod: params.paymentMethod,
      ramp: params.ramp,
    };
    // Remove undefined values
    Object.keys(queryParams).forEach(key =>
      queryParams[key] === undefined && delete queryParams[key]
    );
    const searchParams = new URLSearchParams(queryParams);
    return apiClient.get(`/api/onramp/quotes?${searchParams.toString()}`);
  }

  /**
   * Compare quotes from all providers with metadata
   * Uses GET /quotes endpoint and transforms response
   * @param {Object} params - Quote parameters
   * @returns {Promise<Object>} AggregatedQuoteResponse with quotes and meta
   */
  async compareQuotes(params) {
    const quotes = await this.getAllQuotes(params);

    // Transform Onramper-style quotes to comparison format
    const successfulQuotes = (quotes || []).filter(q => !q.errors);
    const failedQuotes = (quotes || []).filter(q => q.errors);

    // Find the best quote (highest payout)
    const bestQuote = successfulQuotes.length > 0
      ? successfulQuotes.reduce((best, q) =>
          (q.payout || 0) > (best.payout || 0) ? q : best
        )
      : null;

    return {
      quotes: successfulQuotes.map(q => ({
        provider: q.ramp,
        providerDisplayName: this._formatProviderName(q.ramp),
        fiatAmount: params.fiatAmount,
        cryptoAmount: q.payout,
        exchangeRate: q.rate,
        fees: {
          network: q.networkFee || 0,
          service: q.transactionFee || 0,
          total: (q.networkFee || 0) + (q.transactionFee || 0),
        },
        estimatedDeliveryMinutes: q.estimatedDeliveryMinutes,
        widgetSupported: q.widgetSupported,
        recommendations: q.recommendations || [],
        availablePaymentMethods: q.availablePaymentMethods || [],
      })),
      bestQuote: bestQuote ? {
        provider: bestQuote.ramp,
        cryptoAmount: bestQuote.payout,
      } : null,
      meta: {
        requestedAt: new Date().toISOString(),
        fiatCurrency: params.fiatCurrency,
        cryptoCurrency: params.cryptoCurrency,
        network: params.network,
        fiatAmount: params.fiatAmount,
        providersQueried: (quotes || []).map(q => q.ramp),
        providersResponded: successfulQuotes.map(q => q.ramp),
        providersFailed: failedQuotes.map(q => q.ramp),
      },
    };
  }

  /**
   * Format provider name for display
   * @private
   */
  _formatProviderName(provider) {
    const names = {
      transak: 'Transak',
      banxa: 'Banxa',
      'onramp-money': 'Onramp.money',
      'alchemy-pay': 'Alchemy Pay',
      onmeta: 'OnMeta',
      moonpay: 'MoonPay',
      ramp: 'Ramp Network',
    };
    return names[provider?.toLowerCase()] || provider;
  }
}

export default new OnRampService();
